import { TranCategoryKey } from '../models/transaction-metadata.model'
import { EditTransactionMode } from './edit-transaction.projection'

/**
 * Which `category` a request names on the wire — the ONE place that rule lives (PVAL-26440).
 *
 * helios routes a request by its `category`: absent means Enfusion's COMMON trade endpoint, and a
 * value names a dedicated one. Only two tran categories have a dedicated endpoint of their own for
 * an EXISTING record — a Listed trade (`/trade/listed/...`) and a GL journal entry — so those are the
 * only keys an edit or a read of an existing record ever names. Every other tran category is read and
 * edited through the common endpoint, which is why sending `IRS` there would be a rejection rather
 * than a different view.
 *
 * Creating (or posting, which creates) is different: the server has nothing to infer the instrument
 * from, so a create always names its tran category (`categoryRequiredForCreate`, helios !518).
 *
 * Adding a key to {@link DEDICATED_CATEGORIES} changes both the save and the trade read
 * (`HttpTradeReadService`) at once — which is the point of holding it here, and also the thing to
 * know: RPS rejects every read `category` except LISTED today.
 */
export const DEDICATED_CATEGORIES: ReadonlySet<TranCategoryKey> =
  new Set<TranCategoryKey>(['LISTED', 'GL_ENTRY'])

/** The `category` an EXISTING record is addressed by — its key when dedicated, else none. */
export function categoryOfExisting(
  key?: TranCategoryKey | null,
  dedicated: ReadonlySet<TranCategoryKey> = DEDICATED_CATEGORIES,
): TranCategoryKey | undefined {
  return key != null && dedicated.has(key) ? key : undefined
}

/**
 * The `category` a save names, by dialog mode.
 *
 *  - `create` / `post` — always the tran category: a new record has nothing else to route on.
 *  - `edit` — {@link categoryOfExisting}: the key only for a dedicated category, otherwise absent.
 */
export function saveCategory(
  mode: EditTransactionMode,
  key?: TranCategoryKey | null,
  dedicated: ReadonlySet<TranCategoryKey> = DEDICATED_CATEGORIES,
): TranCategoryKey | undefined {
  if (mode === 'edit') return categoryOfExisting(key, dedicated)
  return key == null || key === '' ? undefined : key
}

const text = (value: unknown): string =>
  value == null ? '' : String(value).trim()

/**
 * The id an existing record is saved under: `gLEntryId` first, else `tradeId` — the same precedence
 * the workbench grid reads records by (`recordSources`). Trimmed text, or null when neither is set.
 */
export function recordIdOf(
  fields: Readonly<Record<string, unknown>> | null | undefined,
): string | null {
  if (!fields) return null
  const gl = text(fields['gLEntryId'])
  if (gl) return gl
  const trade = text(fields['tradeId'])
  return trade || null
}
